import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageShell, DataTable, StatusPill, Pill,
  LoadingBox, ErrorBox, InfiniteSentinel,
  useAdminApi, useInfiniteList,
  ActionButton, IconButton, useConfirmDialog, Field, Option,
  Note, Modal, Icon, DateCell, Avatar,
} from "./_adminApi";

const ROLE = {
  superuser: ["violet", "Superuser"],
  staff:     ["blue",   "Staff"],
  regular:   ["neutral","Regular"],
};

function roleOf(u) {
  if (u.is_superuser) return "superuser";
  if (u.is_staff) return "staff";
  return "regular";
}

export default function UsersList() {
  const navigate = useNavigate();
  const api = useAdminApi();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [active, setActive] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [confirm, confirmModal] = useConfirmDialog();
  const [toast, setToast] = useState(null);

  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (role) params.set("role", role);
  if (active) params.set("active", active);
  params.set("_", String(nonce));
  const url = `admin/users/?${params.toString()}`;
  const { items, loadMore, loading, err, hasMore, initialized, total } = useInfiniteList(url);
  const refresh = () => setNonce((n) => n + 1);

  const deleteUser = async (u) => {
    const ok = await confirm({
      title: `Delete ${u.first_name || u.username}?`,
      body: `This account will lose access everywhere. It can't be undone.`,
      detail: u.is_superuser ? "⚠ SUPERUSER — losing this account removes top-level access." : null,
      confirmLabel: "Delete user",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/users/${u.id}/`);
      refresh();
    } catch (e) { alert(e?.response?.data?.detail || e?.response?.data?.error || e.message); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a NEW password for ${u.username}:\n\n(Minimum 8 characters. Hand it over manually — no email is sent.)`);
    if (!pw) return;
    try {
      await api.post(`admin/users/${u.id}/reset-password/`, { password: pw });
      alert(`Password reset for ${u.username}.`);
    } catch (e) { alert(e?.response?.data?.error || e.message); }
  };

  return (
    <PageShell
      title="Users"
      subtitle="Every platform account — admin dashboard, pharmacist and IMS staff. Full CRUD so you never need Django admin."
      actions={
        <ActionButton icon={<Icon.userPlus />} onClick={() => setShowCreate(true)}>
          New user
        </ActionButton>
      }
    >
      <Note>
        <strong>Who should be in this list?</strong> Every account that can log in anywhere.
        Role pills: <Pill kind="violet">Superuser</Pill> full access,{" "}
        <Pill kind="blue">Staff</Pill> this admin dashboard,{" "}
        <Pill kind="neutral">Regular</Pill> app-only.
      </Note>

      <div className="ad-card">
        <div className="ad-card-head">
          <div className="ad-tools" style={{ flex: 1 }}>
            <label className="ad-search">
              <Icon.search />
              <input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className="ad-select">
              <select value={role} onChange={(e) => setRole(e.target.value)} title="Filter by role">
                <option value="">All roles</option>
                <option value="superuser">Superuser</option>
                <option value="staff">Staff</option>
                <option value="regular">Regular</option>
              </select>
            </div>
            <div className="ad-select">
              <select value={active} onChange={(e) => setActive(e.target.value)} title="Filter by active">
                <option value="">All statuses</option>
                <option value="true">Active</option>
                <option value="false">Revoked</option>
              </select>
            </div>
          </div>
        </div>

        {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
        {!initialized && !err && <LoadingBox />}
        {initialized && (
          <>
            <DataTable
              rows={items}
              emptyText="No users match these filters."
              columns={[
                { label: "Name", render: (u) => (
                  <div className="who" onClick={() => navigate(`/dashboard/admin/users/${u.id}`)} style={{ cursor: "pointer" }}>
                    <Avatar name={`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username} id={u.id} active={u.is_active} />
                    <div style={{ minWidth: 0 }}>
                      <div className="t-strong">
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.username}
                      </div>
                      <div className="t-sub">{u.email}</div>
                    </div>
                  </div>
                )},
                { label: "Username", render: (u) => <span className="mono">{u.username}</span> },
                { label: "Role", render: (u) => {
                  const r = roleOf(u); const m = ROLE[r] || ROLE.regular;
                  return <Pill kind={m[0]}>{m[1]}</Pill>;
                }},
                { label: "Status", render: (u) => <StatusPill status={u.is_active ? "active" : "revoked"} /> },
                { label: "Last login", render: (u) => <DateCell iso={u.last_login} /> },
                { label: "Joined", render: (u) => <DateCell iso={u.date_joined} /> },
                { label: "Actions", align: "right", render: (u) => (
                  <div className="ad-actions" onClick={(e) => e.stopPropagation()}>
                    <IconButton title="Reset password" onClick={() => resetPassword(u)}><Icon.key /></IconButton>
                    <IconButton tone="danger" title="Delete" onClick={() => deleteUser(u)}><Icon.trash /></IconButton>
                  </div>
                )},
              ]}
            />
            <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
          </>
        )}

        {initialized && items.length > 0 && (
          <div className="ad-tfoot">
            <span>Showing <b style={{ color: "var(--fg-1)" }}>{items.length}</b>{typeof total === "number" ? ` of ${total}` : ""} users</span>
            {hasMore && <span className="t-muted">Scroll to load more</span>}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); refresh(); }} />
      )}
      {confirmModal}
      {toast && <div className="ad-toast"><Icon.check />{toast}</div>}
    </PageShell>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const api = useAdminApi();
  const [f, setF] = useState({
    username: "", email: "", password: "",
    first_name: "", last_name: "",
    is_staff: false, is_superuser: false, is_active: true,
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const ok = f.username.trim() && f.email.trim() && f.password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      await api.post("admin/users/", f);
      onDone();
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  return (
    <Modal
      title="Create new user"
      icon={Icon.userPlus}
      onClose={onClose}
      desc="The user ID and profile row are generated automatically. Hand the username and password to the person manually — no welcome email is sent."
      footer={
        <>
          <button className="ad-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ad-btn primary" disabled={!ok || saving} onClick={submit}>
            <Icon.userPlus />{saving ? "Creating…" : "Create user"}
          </button>
        </>
      }
    >
      {err && <ErrorBox error={err} />}
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Username" hint="Used to sign in" full>
            <input className="form-control" value={f.username} onChange={(e) => set("username", e.target.value)} autoFocus required />
          </Field>
          <Field label="Email" hint="Unique; used for display">
            <input className="form-control" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required />
          </Field>
          <Field label="Password" hint="Minimum 8 characters">
            <input className="form-control" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required minLength={8} />
          </Field>
          <Field label="First name">
            <input className="form-control" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last name">
            <input className="form-control" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
          <div className="ad-divider" />
          <Field label="Access" full>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Option on={f.is_active} onClick={() => set("is_active", !f.is_active)}
                title="Account active"
                desc="Uncheck to create but immediately disable login." />
              <Option on={f.is_staff} onClick={() => set("is_staff", !f.is_staff)}
                title={<>Staff member <Pill kind="blue">Staff</Pill></>}
                desc="Can access this admin dashboard." />
              <Option on={f.is_superuser} danger onClick={() => set("is_superuser", !f.is_superuser)}
                title={<>Superuser <Pill kind="violet">Superuser</Pill></>}
                desc="Full access everywhere. Grant sparingly." warn />
            </div>
          </Field>
        </div>
      </form>
    </Modal>
  );
}
